import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Mail, MapPin } from "lucide-react";
import { Overline, Reveal, MaskLine } from "./shared";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border-b border-ink/20 bg-transparent py-3 text-base font-light text-ink placeholder:text-moss/60 outline-none transition-colors duration-300 focus:border-ink";

export default function Enquiry() {
  const { t } = useLang();
  const options = t("enquiry.options");
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [productIdx, setProductIdx] = useState(0);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error(t("enquiry.toastValidation"));
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, product: options[productIdx] });
      toast.success(t("enquiry.toastSuccess"));
      setForm({ name: "", phone: "", message: "" });
      setProductIdx(0);
    } catch {
      toast.error(t("enquiry.toastError"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="enquiry" className="border-t border-ink/10 bg-sand/60 py-24 lg:py-32" data-testid="enquiry-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-5">
          <Overline>{t("enquiry.overline")}</Overline>
          <h2 className="mt-4 font-serif text-4xl font-medium leading-tight tracking-tight sm:text-5xl">
            <MaskLine>{t("enquiry.title1")}</MaskLine>
            <MaskLine delay={0.12}>
              <span className="italic text-terracotta">{t("enquiry.title2")}</span>
            </MaskLine>
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-sm text-base font-light leading-relaxed text-moss">
              {t("enquiry.body")}
            </p>
          </Reveal>

          <Reveal delay={0.3}>
            <div className="mt-10 space-y-5">
              <a
                href="https://wa.me/917987140498?text=Namaste%20Arniveda!%20I%20would%20like%20to%20order%20your%20oils."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-cta-button"
                className="group flex items-center gap-4 border border-ink/15 bg-cream px-6 py-4 transition-colors duration-300 hover:border-olive hover:bg-olive hover:text-cream"
              >
                <MessageCircle className="h-5 w-5 text-olive transition-colors group-hover:text-cream" />
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] opacity-70">{t("enquiry.whatsapp")}</p>
                  <p className="font-medium">+91 79871 40498</p>
                </div>
              </a>
              <a
                href="mailto:customers@arniveda.farm"
                data-testid="email-cta-button"
                className="group flex items-center gap-4 border border-ink/15 bg-cream px-6 py-4 transition-colors duration-300 hover:border-terracotta hover:bg-terracotta hover:text-cream"
              >
                <Mail className="h-5 w-5 text-terracotta transition-colors group-hover:text-cream" />
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] opacity-70">{t("enquiry.email")}</p>
                  <p className="font-medium">customers@arniveda.farm</p>
                </div>
              </a>
              <div className="flex items-start gap-4 px-1 pt-2 text-sm font-light text-moss">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                <p>A 604, Elixer Mk City, New Sirol Road, City Centre, Gwalior, Madhya Pradesh 474001</p>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.25} className="lg:col-span-7">
          <form onSubmit={submit} className="border border-ink/10 bg-cream p-8 sm:p-12" data-testid="enquiry-form">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
              <div>
                <label htmlFor="enquiry-name" className="text-xs font-semibold uppercase tracking-[0.18em] text-moss">{t("enquiry.name")}</label>
                <input id="enquiry-name" data-testid="enquiry-name-input" value={form.name} onChange={set("name")} placeholder={t("enquiry.namePh")} className={inputCls} required />
              </div>
              <div>
                <label htmlFor="enquiry-phone" className="text-xs font-semibold uppercase tracking-[0.18em] text-moss">{t("enquiry.phone")}</label>
                <input id="enquiry-phone" data-testid="enquiry-phone-input" value={form.phone} onChange={set("phone")} placeholder={t("enquiry.phonePh")} className={inputCls} required />
              </div>
            </div>
            <div className="mt-8">
              <label htmlFor="enquiry-product" className="text-xs font-semibold uppercase tracking-[0.18em] text-moss">{t("enquiry.product")}</label>
              <select id="enquiry-product" data-testid="enquiry-product-select" value={productIdx} onChange={(e) => setProductIdx(Number(e.target.value))} className={`${inputCls} cursor-pointer`}>
                {options.map((p, i) => (
                  <option key={p} value={i}>{p}</option>
                ))}
              </select>
            </div>
            <div className="mt-8">
              <label htmlFor="enquiry-message" className="text-xs font-semibold uppercase tracking-[0.18em] text-moss">{t("enquiry.message")}</label>
              <textarea id="enquiry-message" data-testid="enquiry-message-input" value={form.message} onChange={set("message")} placeholder={t("enquiry.messagePh")} rows={3} className={`${inputCls} resize-none`} />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="enquiry-submit-button"
              className="mt-10 w-full rounded-full bg-ink px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-terracotta disabled:opacity-50 sm:w-auto"
            >
              {sending ? t("enquiry.sending") : t("enquiry.submit")}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
